import PromisifySync from './mixins/promisify-sync.js';
import validate from 'validate.js';
import validators from 'helpers/validators.js';
import moment from 'moment';
import Log from 'lib/log';
import config from 'config';

// extend validators with a set of our own
// TODO turn into a library so we can just do
// `import validate from 'lib/validate'` and not have to do this here.
Object.assign(validate.validators, validators || {});

const log = Log('base-model');

export default class extends PromisifySync(Backbone.Model) {
    constructor() {
        super(...arguments);
        // by default all models are soft deletable
        this.softDelete = true;
        return this;
    }

    baseUrl() {
        return config.apiUrl + _.result(this, 'urlRoot');
    }
    finalUrl() {
        return config.apiUrl + _.result(this, 'url');
    }

    constraints() {
        return {};
    }

    initialize() {
        super.initialize(...arguments);
        if (this.get(this.idAttribute)) {
            this.id = this.get(this.idAttribute);
        }

        this.unsavedAttributes = {};
        this.on('change', this.trackChangedAttributes, this);
        this.on('sync', this.resetChangedAttributes, this);
        return this;
    }

    setAdditionalConstraints(additionalConstraints) {
        this.additionalConstraints = additionalConstraints;
    }

    validate(attrs, options = {}) {
        options = Object.assign({validateAttrs: {}}, options);
        const pickConstraints = (constraints) => {
            const keys = options.validateAttrs.include ||
                (options.validateAttrs.exclude ?
                    Object.keys(attrs)
                        .filter(attr =>
                            !options.validateAttrs.exclude.includes(attr)
                        ) : Object.keys(attrs));
            const r = {};
            keys.forEach(key => r[key] = constraints[key]);
            return r;
        };
        const constraints = (
            options.validateAttrs.include || options.validateAttrs.exclude || []
        ).length ? pickConstraints(this.constraints()) : this.constraints();

        if (this.additionalConstraints) {
            const ret = Object.assign(
                {},
                validate(attrs, constraints),
                validate(attrs, this.additionalConstraints)
            );
            return Object.keys(ret).length === 0 ? false : ret;
        }
        return validate(attrs, constraints) || false;
    }

    wasValid() {
        return (
            this.validationError === false ||
            !Object.keys(this.validationError || {}).length
        );
    }

    isValid(options) {
        this.validationError = this.validate(this.attributes, options);
        return this.validationError === false;
    }

    getFormatted(attr, format) {
        var value = this.get(attr);
        return moment(value).format(format);
    }

    // We used to check `this.changedAttributes()` but according to the
    // documentation, it tracks changes after every call to set(). Because
    // of this it was not really suiting our needs. `dirty()` now returns
    // true only if there have been changes after `sync`.
    trackChangedAttributes(model) {
        this.unsavedAttributes = Object.assign(
            this.unsavedAttributes,
            model.changed
        );
    }
    resetChangedAttributes() {
        this.unsavedAttributes = {};
    }
    dirty() {
        const isDirty = !!Object.keys(this.unsavedAttributes).length;
        log.debug(
            `Checking dirtyness for ${this.id}:`,
            isDirty ? `It's dirty: ` : `All clean.`,
            Object.keys(this.unsavedAttributes).join(`, `)
        );
        return isDirty;
    }

    dirtyKeys() {
        return Object.keys(this.unsavedAttributes || {});
    }

    set(attr, value, options = false) {
        let changes = {};
        if (typeof attr === 'string') {
            changes[attr] = value;
        } else if (typeof value === 'object' && options === false) {
            options = value;
        }

        if (typeof attr === 'object') {
            changes = attr;
        }

        options = Object.assign({
            validateAttrs: {include: Object.keys(changes)},
            validate: true
        }, options || {});

        // If the attribute has an object value backbone doesn't trigger
        // a change event so we add it to unsavedAttributes manually
        if (typeof attr === 'string' && typeof value === 'object') {
            this.unsavedAttributes = Object.assign(
                this.unsavedAttributes,
                changes
            );
        }

        Object.keys(changes).forEach(attr => {
            if (this.validationError && this.validationError[attr]) {
                delete this.validationError[attr];
            }
        });

        return super.set(changes, options);
    }

    // We need to send the patch option so that backbone uses `PATCH`
    // requests instead of `PUT` (which is currently not supported)
    save(attrs, options = {}) {
        // if the model is new we send whatever came as the first argument
        // (if we send `this.unsavedAttributes || {}` backbone does a PATCH
        // request instead of POST for new models), else we try to send only
        // what was modified in the client.
        const attributes = this.isNew() ?
            attrs :
            attrs || this.unsavedAttributes || {};

        return super.save(
            attributes,
            Object.assign(options, {patch: true})
        );
    }

    // To all requests that go through Backbone: add access token if present,
    // and set the URL
    sync(method, model, options) {
        if (app.user) {
            const accessToken = app.user.getAccessToken();
            options = Object.assign({}, options || {}, {url: model.finalUrl()});
            if (accessToken && accessToken.id) {
                options = Object.assign({}, options, {
                    headers: {'Authorization': accessToken.id}
                });
            }
        }

        return super.sync(method, model, options);
    }

    destroy() {
        if (this.softDelete !== true) {
            return super.destroy(...arguments);
        }

        return this.save(
            {hiddenAt: new Date()},
            {silent: true}
        );
    }
}



// WEBPACK FOOTER //
// ./app/new-models/model.js